// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import { useEffect, useId, useRef, useState, type KeyboardEvent } from 'react';

import type { Sample, SampleResult, SurveyType } from '../../../api/types';
import { useCanEdit } from '../../../app/CaseRoleContext';
import { editorKeyAction } from '../../../app/editorKeys';
import { kindOf } from '../../../app/keyTargets';
import { useArmedConfirm } from '../../../app/useArmedConfirm';
import { fieldKeys, useTextDraft } from '../../../app/useTextDraft';
import { tokenVar } from '../../../bygning/lens';
import { typeColorToken } from '../../../bygning/typeColors';
import { ENV_LABEL, ENV_TONE, RESULT_LABEL, STAGE_LABEL } from '../../../kortlaegning/vocab';
import { answeredNote, cardAction, deleteConfirmText, nextStage, statusPill, type SampleLinks } from '../../../miljoe/model';
import { Button } from '../../Button';
import { LinkPicker } from '../../miljoe/LinkPicker';
import { Pill } from '../../Pill';
import { TextInput } from '../../TextInput';
import { TextLink } from '../../TextLink';
import { advanceLabel, firstPlus, linkedItems, sampleRooms, timeline } from './sampleInfo';
import styles from './SampleInspector.module.css';

export interface SampleInspectorProps {
  sample: Sample;
  types: SurveyType[];
  /** The links to show: the pending link draft, else the server's (`linksOf`). */
  links: SampleLinks;
  /** A write is queued or in flight: gates the buttons (never the field commits). */
  busy: boolean;
  onAdvance: () => void;
  onResult: (result: SampleResult) => void;
  onUndoResult: () => void;
  onTitle: (title: string) => void;
  onWhat: (what: string) => void;
  onToggleLink: (typeId: number) => void;
  /** Link or unlink a single part (a bygningsdel, not its whole type). */
  onTogglePart: (code: string) => void;
  onDelete: () => void;
  /** A linked type (or a linked part's type, with the part focused): open it in Ressourcer. */
  onOpenType: (typeId: number, partCode: string | null) => void;
}

/**
 * The sample inspector (v8 `rightPrv`): code, status pill, title and rooms;
 * the "Kæde" timeline; the result note; the whole types and single parts it
 * is "Knyttet til" (frontend-r22.3); and
 * the one action the stage allows. "Rediger" opens title, what, the link
 * picker and delete (an armed two-click confirm).
 *
 * Owns its editor's text drafts (`useTextDraft`), so render it with
 * `key={sample.id}` — a draft must never move to another sample.
 */
export function SampleInspector(props: SampleInspectorProps) {
  const { sample, types, links, busy, onAdvance, onResult, onUndoResult, onOpenType } = props;
  const canEdit = useCanEdit();
  const id = useId();
  const headingId = `${id}-h`;
  const editorId = `${id}-editor`;
  const [editing, setEditing] = useState(false);
  const editButton = useRef<HTMLButtonElement>(null);
  const pill = statusPill(sample);
  const action = cardAction(sample);
  const next = nextStage(sample.stage);
  const linked = linkedItems(links, types);
  const rooms = firstPlus(sampleRooms(links, types));
  const steps = timeline(sample);

  function closeEditor() {
    setEditing(false);
    editButton.current?.focus();
  }

  return (
    <div className={styles.root}>
      <header className={styles.ttl}>
        <h2 id={headingId} className={styles.code}>
          {sample.code}
        </h2>
        <Pill tone={pill.tone}>{pill.label}</Pill>
        {canEdit && (
          <TextLink
            ref={editButton}
            size="sm"
            className={styles.end}
            aria-expanded={editing}
            aria-controls={editing ? editorId : undefined}
            aria-describedby={headingId}
            onClick={() => (editing ? closeEditor() : setEditing(true))}
          >
            {editing ? 'Luk redigering' : 'Rediger'}
          </TextLink>
        )}
      </header>
      <p className={styles.title}>
        {sample.title}
        {rooms && <span className={styles.muted}> · {rooms}</span>}
      </p>
      {sample.what && <p className={styles.what}>{sample.what}</p>}

      {canEdit && editing && (
        <SampleEditor
          {...props}
          id={editorId}
          onClose={closeEditor}
        />
      )}

      <section className={styles.sec} aria-labelledby={`${id}-chain`}>
        <h3 id={`${id}-chain`} className={styles.heading}>
          Kæde
        </h3>
        <ol className={styles.tl} aria-label="Prøveforløb">
          {steps.map((s) => (
            <li key={s.stage} className={styles.tlStep} aria-current={s.state === 'current' ? 'step' : undefined}>
              <span className={`${styles.dot} ${styles[s.state]}`} aria-hidden="true" />
              <span className={styles.tlText}>
                {s.label}
                <small className={styles.tlNote}>{s.note}</small>
              </span>
            </li>
          ))}
        </ol>
      </section>

      {sample.result && (
        <p className={sample.result === 'forurenet' ? `${styles.note} ${styles.crit}` : `${styles.note} ${styles.good}`}>
          <b>Svar: {RESULT_LABEL[sample.result]}.</b> {answeredNote(links, types)}
        </p>
      )}

      <section className={styles.sec} aria-labelledby={`${id}-linked`}>
        <h3 id={`${id}-linked`} className={styles.heading}>
          Knyttet til
        </h3>
        {linked.length === 0 ? (
          <p className={styles.muted}>
            Ikke koblet til en type eller del{canEdit ? ' — vælg typer eller dele under Rediger.' : '.'}
          </p>
        ) : (
          <ul className={styles.types}>
            {linked.map(({ key, type: t, partCode, label }) => (
              <li key={key}>
                {t.review_status === 'rejected' ? (
                  <span className={styles.typeRow}>
                    <span className={styles.typeName}>{label} (afvist)</span>
                  </span>
                ) : (
                  <button
                    type="button"
                    className={`${styles.typeRow} ${styles.typeBtn}`}
                    onClick={() => onOpenType(t.id, partCode)}
                    title={partCode ? `Åbn ${partCode} i Ressourcer` : `Åbn ${t.name} i Ressourcer`}
                  >
                    <i className={styles.swatch} style={{ background: tokenVar(typeColorToken(t)) }} aria-hidden="true" />
                    <span className={styles.typeName}>
                      {partCode ? <span className="mono">{partCode}</span> : label}
                      {partCode && <span className={styles.muted}>{label.slice(partCode.length)}</span>}
                    </span>
                    <Pill tone={ENV_TONE[t.environment_status]}>{ENV_LABEL[t.environment_status]}</Pill>
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <div className={styles.sp} />

      {canEdit && action === 'advance' && (
        <div className={styles.act}>
          <Button
            variant="primary"
            size="lg"
            disabled={busy}
            onClick={onAdvance}
            aria-describedby={headingId}
            title={next ? `Markér som ${STAGE_LABEL[next]}` : undefined}
          >
            {advanceLabel(sample.stage) ?? 'Næste trin'}
          </Button>
        </div>
      )}
      {canEdit && action === 'answer' && (
        <div className={styles.act}>
          <Button variant="primary" size="lg" disabled={busy} onClick={() => onResult('ren')} aria-describedby={headingId}>
            Registrér svar: Ren
          </Button>
          <Button size="lg" disabled={busy} onClick={() => onResult('forurenet')} aria-describedby={headingId}>
            Registrér svar: Forurenet
          </Button>
        </div>
      )}
      {action === 'answered' && (
        <div className={styles.act}>
          <span className={styles.finished}>Prøven er afsluttet</span>
          {canEdit && (
            <Button size="lg" disabled={busy} onClick={onUndoResult} aria-describedby={headingId}>
              Fortryd svar
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface SampleEditorProps extends SampleInspectorProps {
  id: string;
  onClose: () => void;
}

/** Title, what was sampled, the links and delete. Fields commit on blur; Esc reverts a field, then closes. */
function SampleEditor({ sample, types, links, busy, onTitle, onWhat, onToggleLink, onTogglePart, onDelete, onClose, id }: SampleEditorProps) {
  const title = useTextDraft(sample.title, onTitle, true);
  const what = useTextDraft(sample.what, onWhat);
  const editorRef = useRef<HTMLDivElement>(null);
  const titleInput = useRef<HTMLInputElement>(null);
  const confirm = useArmedConfirm<number>(busy, sample.id);
  const armed = confirm.armed === sample.id;

  // Opening the editor lands on Titel.
  useEffect(() => {
    titleInput.current?.focus();
  }, []);

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const action = editorKeyAction({ key: e.key, kind: kindOf(e.target), ctrlKey: e.ctrlKey, metaKey: e.metaKey, altKey: e.altKey });
    if (action === 'close' || action === 'submit') {
      // An armed delete takes the first Esc (useArmedConfirm disarms); the next one closes.
      if (armed && action === 'close') return;
      e.preventDefault();
      e.stopPropagation(); // handled here: the shell's Esc must not also step out
      if (e.target instanceof HTMLElement) e.target.blur();
      onClose();
    }
  }

  return (
    // tabIndex -1: a focus target for Esc-revert, never a Tab stop.
    <div
      ref={editorRef}
      id={id}
      role="group"
      aria-label={`Rediger ${sample.code}`}
      tabIndex={-1}
      className={styles.editor}
      onKeyDown={onKeyDown}
    >
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${id}-title`}>
          Titel
        </label>
        <TextInput
          ref={titleInput}
          id={`${id}-title`}
          variant="filled"
          size="lg"
          block
          {...title.props}
          onKeyDown={fieldKeys(title, editorRef)}
        />
      </div>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${id}-what`}>
          Hvad er udtaget, og hvor
        </label>
        <TextInput
          id={`${id}-what`}
          variant="filled"
          size="lg"
          block
          {...what.props}
          onKeyDown={fieldKeys(what, editorRef)}
        />
      </div>
      <p className={styles.hint}>
        Et prøvesvar opdaterer miljøstatus på alle koblede typer i kortlægningen — også typen for hver enkelt koblet del. Én
        prøve kan afklare mange bygningsdele.
      </p>
      <LinkPicker types={types} selected={links} onToggle={onToggleLink} onTogglePart={onTogglePart} />
      {armed && (
        <p className={styles.confirmText} role="alert">
          {deleteConfirmText({ code: sample.code, title: sample.title, ...links }, types)}
        </p>
      )}
      <div className={styles.editorActions}>
        <Button
          tone="danger"
          size="lg"
          armed={armed}
          disabled={busy}
          onClick={(e) => {
            if (armed) {
              confirm.disarm();
              onDelete();
            } else {
              confirm.arm(sample.id, e.currentTarget);
            }
          }}
          onBlur={confirm.disarm}
        >
          {armed ? 'Bekræft: slet prøven' : 'Slet prøve'}
        </Button>
        <Button size="lg" onClick={onClose}>
          Luk
        </Button>
      </div>
    </div>
  );
}
